import React, { useEffect, useMemo, useRef, useState } from "react";
import type { TowerShipment } from "../../types/controlTower";
import type { RouteStop } from "./driverRoute";
import { addressesText, LatLng, shipmentsInside } from "./mapSelection";
import { useLeaflet } from "../../hooks/useLeaflet";
import { hasCoordinates } from "./towerShared";
import { addRouteStops, addShipmentMarker, statusTone } from "./shipmentMarkers";
import MapLegend from "./MapLegend";
import { removeMap } from "./TowerFocusMap";

const ZONE_COLOR = "#d6a84a";

// Con `route`, el mapa muestra el recorrido: paradas numeradas en el orden en
// que se visitaron, unidas por una línea. Sin `route`, los envíos por estado.
// focus: el envío al que hay que ir ("Ver en mapa"); seq cambia en cada pedido.
export default function TowerShipmentsMap({
  shipments,
  lastDelivery = null,
  route = null,
  focus = null,
}: {
  shipments: TowerShipment[];
  lastDelivery?: TowerShipment | null;
  route?: RouteStop[] | null;
  focus?: { id: string; seq: number } | null;
}) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any>(null);
  const zoneLayerRef = useRef<any>(null);
  const lastFitKey = useRef("");
  const markerById = useRef(new Map<string, any>());
  // Pedido de "Ver en mapa" que todavía no se pudo aplicar (el marcador no estaba).
  const pendingFocus = useRef<string | null>(null);
  const applyFocus = () => {
    const id = pendingFocus.current;
    const marker = id ? markerById.current.get(id) : null;
    if (!marker || !mapRef.current) return;
    pendingFocus.current = null;
    mapRef.current.setView(marker.getLatLng(), 16, { animate: true });
    marker.openPopup();
  };
  const [mapLayoutVersion, setMapLayoutVersion] = useState(0);
  // Selección por zona: se dibuja a mano alzada y quedan marcados los envíos de adentro.
  const [drawing, setDrawing] = useState(false);
  const [zone, setZone] = useState<LatLng[] | null>(null);
  const [copied, setCopied] = useState<"ok" | "error" | null>(null);
  const points = useMemo(
    () =>
      shipments.filter(
        (shipment) =>
          shipment.latitude !== null &&
          shipment.longitude !== null &&
          Number.isFinite(shipment.latitude) &&
          Number.isFinite(shipment.longitude),
      ),
    [shipments],
  );
  const hasLastDeliveryCoordinates = hasCoordinates(lastDelivery);
  const routeStops = useMemo(() => (route || []).filter((stop) => hasCoordinates(stop.shipment)), [route]);
  // Con recorrido, los visitados se ven como paradas numeradas y, además, lo
  // que queda por visitar (en camino o pendiente) con su color de estado. Un
  // cancelado u otro estado sin visita no es una parada pendiente: no se dibuja.
  const mapPoints = useMemo(() => {
    if (route) {
      const visited = new Set(route.map((stop) => stop.shipment.id));
      return points.filter(
        (point) => !visited.has(point.id) && (point.status === "inTransit" || point.status === "pending"),
      );
    }
    if (!hasCoordinates(lastDelivery) || points.some((point) => point.id === lastDelivery.id)) return points;
    return [...points, lastDelivery];
  }, [points, lastDelivery, route]);
  const selected = useMemo(() => (zone ? shipmentsInside(mapPoints, zone) : []), [mapPoints, zone]);
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const clearZone = () => {
    setZone(null);
    setDrawing(false);
    setCopied(null);
  };
  const copyAddresses = async () => {
    try {
      await navigator.clipboard.writeText(addressesText(selected));
      setCopied("ok");
    } catch {
      setCopied("error");
    }
  };

  // El recorrido no tiene zona dibujada: al entrar se cancela el dibujo.
  useEffect(() => {
    if (route) setDrawing(false);
  }, [route]);

  // Modo dibujo: el mapa deja de arrastrarse, el trazo sigue al mouse y al
  // soltar queda la zona. Esc cancela.
  useEffect(() => {
    const map = mapRef.current;
    if (!leaflet || !map || !drawing || route) return;
    const L = (window as any).L;
    const container = map.getContainer();
    let path: LatLng[] = [];
    let line: any = null;
    const onDown = (event: any) => {
      path = [[event.latlng.lat, event.latlng.lng]];
      line = L.polyline(path, { color: ZONE_COLOR, weight: 2, dashArray: "4 4" }).addTo(map);
    };
    const onMove = (event: any) => {
      if (!line) return;
      path.push([event.latlng.lat, event.latlng.lng]);
      line.setLatLngs(path);
    };
    const finish = () => {
      if (!line) return;
      map.removeLayer(line);
      line = null;
      setDrawing(false);
      setZone(path.length >= 3 ? path : null);
    };
    // Esc cancela solo el trazo, sin cerrar el detalle del chofer.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setDrawing(false);
      }
    };
    map.dragging.disable();
    map.doubleClickZoom.disable();
    container.style.cursor = "crosshair";
    map.on("mousedown", onDown);
    map.on("mousemove", onMove);
    document.addEventListener("mouseup", finish);
    document.addEventListener("keydown", onKey, true);
    return () => {
      map.off("mousedown", onDown);
      map.off("mousemove", onMove);
      document.removeEventListener("mouseup", finish);
      document.removeEventListener("keydown", onKey, true);
      if (line) map.removeLayer(line);
      map.dragging.enable();
      map.doubleClickZoom.enable();
      container.style.cursor = "";
    };
  }, [leaflet, drawing, route]);

  // La zona dibujada queda visible hasta limpiarla.
  useEffect(() => {
    const layer = zoneLayerRef.current;
    if (!leaflet || !layer) return;
    layer.clearLayers();
    if (zone)
      (window as any).L.polygon(zone, { color: ZONE_COLOR, weight: 2, fillColor: ZONE_COLOR, fillOpacity: 0.1 }).addTo(
        layer,
      );
  }, [leaflet, zone, mapLayoutVersion]);

  useEffect(() => {
    const container = containerRef.current;
    if (!leaflet || !container || mapRef.current) return;

    let resizeFrame: number | null = null;
    let lastWidth = 0;
    let lastHeight = 0;

    // The dialog can mount while closed, when its map container has no size.
    // Wait for a measurable layout so Leaflet never fits the shipments to a 0px map.
    const syncMapSize = () => {
      const { width, height } = container.getBoundingClientRect();
      if (width <= 0 || height <= 0 || (width === lastWidth && height === lastHeight && mapRef.current)) return;
      lastWidth = width;
      lastHeight = height;

      if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        const currentSize = container.getBoundingClientRect();
        if (currentSize.width <= 0 || currentSize.height <= 0) return;

        let map = mapRef.current;
        if (!map) {
          const L = (window as any).L;
          map = L.map(container, {
            zoomControl: true,
            scrollWheelZoom: true,
            attributionControl: true,
          }).setView([-34.6, -58.42], 10);
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: "&copy; OpenStreetMap contributors",
          }).addTo(map);
          mapRef.current = map;
          markersRef.current = L.layerGroup().addTo(map);
          zoneLayerRef.current = L.layerGroup().addTo(map);
        } else {
          map.invalidateSize({ pan: false });
        }

        // Re-run marker placement/fitBounds after the map has its real dimensions.
        setMapLayoutVersion((version) => version + 1);
      });
    };

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncMapSize) : null;
    observer?.observe(container);
    window.addEventListener("resize", syncMapSize);
    syncMapSize();

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", syncMapSize);
      if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
      const map = mapRef.current;
      if (map) {
        removeMap(map);
        if (mapRef.current === map) {
          mapRef.current = null;
          markersRef.current = null;
          zoneLayerRef.current = null;
          lastFitKey.current = "";
        }
      }
    };
  }, [leaflet]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = markersRef.current;
    if (!leaflet || !map || !layer) return;
    const L = (window as any).L;
    layer.clearLayers();
    markerById.current.clear();

    for (const shipment of mapPoints) {
      const isLastDelivery = shipment.id === lastDelivery?.id;
      const marker = addShipmentMarker(L, layer, shipment as TowerShipment & { latitude: number; longitude: number }, {
        isLastDelivery,
        isSelected: selectedIds.has(shipment.id),
      });
      markerById.current.set(shipment.id, marker);
    }
    const routeLatLngs = addRouteStops(L, layer, routeStops);

    // Solo se reencuadra si cambian los puntos o el tamaño del mapa, no al
    // seleccionar una zona (el usuario está mirando ese sector).
    const fitPoints = [...(route ? routeLatLngs : []), ...mapPoints.map((point) => [point.latitude, point.longitude])];
    const fitKey = `${mapLayoutVersion}|${routeStops.map((stop) => stop.shipment.id).join(",")}|${mapPoints.map((point) => point.id).join(",")}`;
    if (fitKey !== lastFitKey.current) {
      lastFitKey.current = fitKey;
      if (fitPoints.length === 1) {
        map.setView(fitPoints[0], 14, { animate: false });
      } else if (fitPoints.length > 1) {
        map.fitBounds(L.latLngBounds(fitPoints).pad(0.12), { maxZoom: 13, animate: false });
      }
    }
    // Un "Ver en mapa" pedido antes de que existiera el marcador se aplica ahora,
    // después del encuadre, para que no lo pise.
    applyFocus();
    const resizeFrame = window.requestAnimationFrame(() => {
      if (mapRef.current === map) map.invalidateSize();
    });
    return () => window.cancelAnimationFrame(resizeFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaflet, mapPoints, lastDelivery, route, routeStops, selectedIds, mapLayoutVersion]);

  useEffect(() => {
    if (!focus) return;
    pendingFocus.current = focus.id;
    applyFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.seq]);

  return (
    <div className="tower-map-frame" aria-label="Mapa de envíos del chofer">
      {!route && leaflet && (
        <div className="tower-map-tools" aria-live="polite">
          {drawing ? (
            <>
              <span>Trazá la zona con el mouse y soltá para terminar · Esc cancela</span>
              <button type="button" className="tower-map-filter" onClick={() => setDrawing(false)}>
                Cancelar
              </button>
            </>
          ) : zone ? (
            <>
              <strong>
                {selected.length === 1 ? "1 envío seleccionado" : `${selected.length} envíos seleccionados`}
              </strong>
              <button
                type="button"
                className="tower-map-filter tower-map-action"
                disabled={!selected.length}
                onClick={copyAddresses}
              >
                Copiar direcciones
              </button>
              <button type="button" className="tower-map-filter tower-map-action" onClick={clearZone}>
                Limpiar
              </button>
              {copied && (
                <span className={copied === "ok" ? "tower-green" : "tower-map-copy-error"} role="status">
                  {copied === "ok" ? "Copiadas" : "No se pudo copiar"}
                </span>
              )}
            </>
          ) : (
            <button
              type="button"
              className="tower-map-filter"
              onClick={() => {
                setCopied(null);
                setDrawing(true);
              }}
              title="Dibujá un área a mano alzada para seleccionar los envíos de adentro y copiar sus direcciones."
            >
              Dibujar zona
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          )}
        </div>
      )}
      {!leaflet ? (
        <div className="tower-map-placeholder">Cargando mapa…</div>
      ) : (
        <div ref={containerRef} className="tower-map-canvas" />
      )}
      <MapLegend
        route={Boolean(route)}
        withPending={Boolean(route) && mapPoints.length > 0}
        showPendingStatus={mapPoints.some((s) => s.status === "pending")}
        showRescheduled={mapPoints.some(
          (s) => s.status === "delayed" || s.status === "rescheduled" || statusTone(s) === "rescheduled",
        )}
        showPrevious={shipments.some((s) => s.carryover)}
        lastDelivery={lastDelivery}
        lastDeliveryLocated={hasLastDeliveryCoordinates}
      />
    </div>
  );
}
